import { ExperienceType } from '@/generated/graphql';
import { theme } from '@/app/common/providers/theme-provider/theme';
import styled from '@emotion/styled';
import { Button, Grid, Typography } from '@mui/material';
import { useState } from 'react';

const ExperienceIntro = styled(Grid)`
  width: 100%;
  padding-right: 70px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin-bottom: 100px;
  @media (max-width: 768px) {
    width: 100%;
    padding: 0 20px;
    height: auto;
    margin-bottom: auto;
  }
`;
const ShareMoments = styled.div`
  margin: 0;
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  @media (max-width: 768px) {
    width: 100%;
    display: block;
  }
`;
const InfoIconsDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  column-gap: 20px;
  margin-bottom: 20px;
`;

const Info = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  column-gap: 16px;
  margin-top: 32px;
`;
const InfoTitle = styled(Typography)`
  color: #251628;
  text-align: center;
  font-family: var(--font-work-sans);
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 159.3%;
`;
const InfoIconDiv = styled.div`
  height: 55px;
  width: 55px;
  border-radius: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;
const InfoIcon = styled.img`
  height: 100%;
  width: 100%;
  object-fit: contain;
`;

const ButtonInfoTitle = styled(Typography)`
  text-decoration: none;
  text-transform: none;
  color: #251628;
  text-align: center;
  font-family: var(--font-work-sans);
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 159.3%;
  padding-left: 10px;
`;
const ButtonInfoIconDiv = styled.div`
  height: 55px;
  width: 55px;
  border-radius: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const ShareMomentsHeader = styled(Typography)`
  font-family: var(--font-cooper-light);
  font-style: normal;
  font-weight: 300;
  font-size: 57px;
  line-height: 109.3%;
  color: #231727;
  @media (max-width: 768px) {
    font-size: 40px;
    font-weight: 400;
  }
`;
const ExperienceDescription = styled(Typography)`
  font-family: var(--font-work-sans);
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 159.3%;
  color: #251628;
  text-align: start;
  margin-bottom: 30px;
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

export interface ExperienceIntroProps {
  title: string;
  type: ExperienceType;
  isOnline: boolean;
  description: string;
}
const scrollToElement = (id: string) => {
  const targetPos = document?.getElementById(id)?.offsetTop || 0;
  const initialPos = window.scrollY;
  const distance = targetPos - initialPos;

  scrollTo({
    top: distance,
    behavior: 'smooth',
  });
};

const maxCharLength = 280;

const ExperienceIntroComponent = ({ title, type, isOnline, description }: ExperienceIntroProps) => {
  const [showAll, setShowAll] = useState(false);

  const longerThanMaxCharLength = description.length > maxCharLength;

  const displayDescription =
    longerThanMaxCharLength && !showAll ? `${description.slice(0, maxCharLength)}...` : description;

  return (
    <ExperienceIntro>
      <ShareMoments>
        <ShareMomentsHeader>{title}</ShareMomentsHeader>
        {type !== 'Box' && (
          <InfoIconsDiv>
            <Info>
              <InfoIconDiv>
                <InfoIcon
                  src={
                    type === ExperienceType.Event
                      ? '/experience-pages/group-icon.png'
                      : '/experience-pages/schedule.png'
                  }
                />
              </InfoIconDiv>
              <InfoTitle>{type === ExperienceType.Event ? 'Group' : 'One-on-one'}</InfoTitle>
            </Info>
            <Info>
              {isOnline ? (
                <>
                  <InfoIconDiv>
                    <InfoIcon src={'/experience-pages/online-circle.png'} />
                  </InfoIconDiv>
                  <InfoTitle>{'Online'}</InfoTitle>
                </>
              ) : (
                <Button
                  style={{ backgroundColor: 'transparent' }}
                  onClick={() => {
                    console.log('scrolling');
                    scrollToElement('in-person');
                  }}
                >
                  <ButtonInfoIconDiv style={{ paddingTop: '5px' }}>
                    <InfoIcon src={'/experience-pages/in-person-map.png'} />
                  </ButtonInfoIconDiv>
                  <ButtonInfoTitle>{'In Person'}</ButtonInfoTitle>
                </Button>
              )}
            </Info>
          </InfoIconsDiv>
        )}
        {type === 'Box' && (
          <InfoIconsDiv>
            <Info>
              <InfoIconDiv>
                <InfoIcon src={'/experience-pages/group-new.png'} />
              </InfoIconDiv>
              <InfoTitle>Box</InfoTitle>
            </Info>
          </InfoIconsDiv>
        )}
        <ExperienceDescription>
          {displayDescription}
          {longerThanMaxCharLength && (
            <>
              <br />
              {/*TODO: Fix styling issue*/}
              {/*eslint-disable-next-line jsx-a11y/anchor-is-valid*/}
              <a
                href="#"
                onClick={e => {
                  e.preventDefault();
                  setShowAll(!showAll);
                }}
                style={{ color: theme.palette.common.midnight, fontSize: '16px' }}
                role="button"
                aria-roledescription="toggle full description"
              >
                {showAll ? 'Show less' : 'Show more'}
              </a>
            </>
          )}
        </ExperienceDescription>
      </ShareMoments>
    </ExperienceIntro>
  );
};

export default ExperienceIntroComponent;
